import React, { useRef, useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, Send, RefreshCw, CheckCircle2, Building2, TrendingUp, X } from 'lucide-react';
import { Image, useColorModeValue } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { sendOtpInEmailOrNo, userProfile } from '../../../redux/actions/user';
import { authLogin } from '../../../redux/actions/authentication';
import Logo from '../../../assets/img/zeronine.png';
import { sendOtp as sendOtpAction, forgetPassword } from '../../../redux/actions/user';

function SignIn() {
   const [isDark, setIsDark] = useState(false);

   // Original state variables from your code
   const [email, setEmail] = useState('');
   const [emailForOtp, setEmailForOtp] = useState('');
   const [forgotPasswordOtp, setForgotPasswordOtp] = useState(''); // Separate state for forgot password OTP
   const [twoFactorOtp, setTwoFactorOtp] = useState(''); // Separate state for 2FA OTP
   const otpRef = useRef(null);
   const [newPassword, setNewPassword] = useState('');
   const [showOtpFields, setShowOtpFields] = useState(false);
   const [showPassword, setShowPassword] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   const [otpShow, setOtpShow] = useState(false);
   const [otpMethod, setOtpMethod] = useState('email');
   const [otpSent, setOtpSent] = useState(false);
   const [isCheckingAuth, setIsCheckingAuth] = useState(false);
   const overlayBg = useColorModeValue('rgba(255, 255, 255, 0.8)', 'rgba(0, 0, 0, 0.8)');
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

   // Forgot password modal state
   const [showForgotModal, setShowForgotModal] = useState(false);

   // Form state for react-hook-form replacement
   const [formData, setFormData] = useState({ email: '', password: '', otp: '' });
   const [errors, setErrors] = useState({});

   // Your original useEffect
   useEffect(() => {
      const checkAuthAndRedirect = async () => {
         const tokenData = localStorage.getItem('authTokenCustomer');

         if (tokenData) {
            try {
               const parsedToken = JSON.parse(tokenData);
               await dispatch(userProfile());
               navigate('/admin/default');
            } catch (error) {
               localStorage.removeItem('authTokenCustomer');
            }
         }
         setIsCheckingAuth(false);
      };

      checkAuthAndRedirect();

      const authChannel = new BroadcastChannel('auth');
      authChannel.onmessage = (e) => {
         if (e.data.type === 'LOGIN') {
            checkAuthAndRedirect();
         }
         if (e.data.type === 'LOGOUT') {
            navigate('/auth/sign-in');
         }
      };

      return () => {
         authChannel.close();
      };
   }, []);

   // Your original onSubmit function
   const onSubmit = async (data) => {
      setIsLoading(true);
      try {
         if (otpShow) {
            const loginData = { ...data, otp: twoFactorOtp }; // Use twoFactorOtp for login
            const res = await dispatch(authLogin(navigate, loginData));
            if (res.code === 1) {
               await dispatch(userProfile());
            }
         } else {
            const res = await dispatch(authLogin(navigate, data));
            setEmailForOtp(data.email);
            if (res.data.web2Factor) {
               setOtpShow(true);
            } else {
               await dispatch(userProfile());
            }
         }
      } catch (error) {
         console.error('Login error:', error);
      } finally {
         setIsLoading(false);
      }
   };

   // Your original handleKeyDown function
   const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
         handleSubmit();
      }
   };

   // Your original sendOtp function
   const sendOtp = () => {
      const Data = { email: email };
      dispatch(sendOtpAction(Data));
      setShowOtpFields(true);
      otpRef.current?.focus();
   };

   // Your original resetPassword function
   const resetPassword = () => {
      if (forgotPasswordOtp && newPassword) {
         // Use forgotPasswordOtp
         const formData = { email: email, password: newPassword, otp: Number(forgotPasswordOtp) };
         dispatch(forgetPassword(formData));
      }
      setShowForgotModal(false);
      setEmail('');
      setForgotPasswordOtp(''); // Reset forgot password OTP
      setNewPassword('');
      setShowOtpFields(false);
   };

   // Your original handleSendOtp function
   const handleSendOtp = async () => {
      setOtpSent(true);
      const otpdata = {
         email: emailForOtp,
         sendType: otpMethod === 'email' ? 0 : otpMethod === 'mobile' ? 1 : 2,
      };
      await dispatch(sendOtpInEmailOrNo(otpdata));
   };

   // Handle OTP input for 2FA
   const handleTwoFactorOtpChange = (index, value) => {
      if (value) {
         const newOtp = twoFactorOtp.split('');
         newOtp[index] = value;
         const updatedOtp = newOtp.join('');
         setTwoFactorOtp(updatedOtp);
         if (errors.otp) setErrors({ ...errors, otp: '' });

         // Auto-focus next input
         if (index < 5) {
            document.getElementById(`twoFactor-otp-${index + 1}`)?.focus();
         }
      }
   };

   // Handle OTP input for forgot password
   const handleForgotPasswordOtpChange = (index, value) => {
      if (value) {
         const newOtp = forgotPasswordOtp.split('');
         newOtp[index] = value;
         const updatedOtp = newOtp.join('');
         setForgotPasswordOtp(updatedOtp);
         if (errors.otp) setErrors({ ...errors, otp: '' });

         // Auto-focus next input
         if (index < 5) {
            document.getElementById(`forgot-otp-${index + 1}`)?.focus();
         }
      }
   };

   // Handle backspace for 2FA OTP
   const handleTwoFactorOtpBackspace = (index, e) => {
      if (e.key === 'Backspace' && !twoFactorOtp[index] && index > 0) {
         document.getElementById(`twoFactor-otp-${index - 1}`)?.focus();
      } else if (e.key === 'Backspace' && twoFactorOtp[index]) {
         // Clear current field and stay there
         const newOtp = twoFactorOtp.split('');
         newOtp[index] = '';
         setTwoFactorOtp(newOtp.join(''));
      }
   };

   // Handle backspace for forgot password OTP
   const handleForgotPasswordOtpBackspace = (index, e) => {
      if (e.key === 'Backspace' && !forgotPasswordOtp[index] && index > 0) {
         document.getElementById(`forgot-otp-${index - 1}`)?.focus();
      } else if (e.key === 'Backspace' && forgotPasswordOtp[index]) {
         // Clear current field and stay there
         const newOtp = forgotPasswordOtp.split('');
         newOtp[index] = '';
         setForgotPasswordOtp(newOtp.join(''));
      }
   };

   // Handle paste for 2FA OTP
   const handleTwoFactorOtpPaste = (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
      setTwoFactorOtp(pastedData);
      if (errors.otp) setErrors({ ...errors, otp: '' });

      // Focus last filled box
      const nextIndex = Math.min(pastedData.length, 5);
      document.getElementById(`twoFactor-otp-${nextIndex}`)?.focus();
   };

   // Handle paste for forgot password OTP
   const handleForgotPasswordOtpPaste = (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
      setForgotPasswordOtp(pastedData);
      if (errors.otp) setErrors({ ...errors, otp: '' });

      // Focus last filled box
      const nextIndex = Math.min(pastedData.length, 5);
      document.getElementById(`forgot-otp-${nextIndex}`)?.focus();
   };

   // Handle form submission
   const handleSubmit = () => {
      const newErrors = {};

      if (!formData.email) {
         newErrors.email = 'Email is required';
      }
      if (!formData.password) {
         newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
         newErrors.password = 'Password must be at least 6 characters';
      }
      if (otpShow && !twoFactorOtp) {
         newErrors.otp = 'OTP is required';
      } else if (otpShow && !/^[0-9]{6}$/.test(twoFactorOtp)) {
         newErrors.otp = 'Please enter a valid 6-digit OTP';
      }

      setErrors(newErrors);

      if (Object.keys(newErrors).length === 0) {
         onSubmit(formData);
      }
   };

   if (isCheckingAuth) {
      return (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
            <RefreshCw className="w-12 h-12 animate-spin text-indigo-600" />
         </div>
      );
   }

   return (
      <div
         className={`min-h-screen transition-colors duration-300 relative ${isDark
            ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
            : 'bg-gradient-to-br from-blue-50 via-white to-indigo-50'
            }`}>
         {/* Accounting texture background pattern */}
         <div
            className="absolute inset-0 opacity-5 pointer-events-none"
            style={{
               backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
               backgroundSize: '60px 60px',
            }}
         />
         {/* Loading overlay */}
         {isLoading && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
               <div className={`p-8 rounded-2xl ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                  <RefreshCw className="w-16 h-16 animate-spin text-indigo-600 mx-auto" />
               </div>
            </div>
         )}

         {/* Top Bar: Color Mode Toggle (Left) + Logo (Right) */}
         <div className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center p-6 sm:p-8">
            {/* Color Mode Toggle - Left */}
            <button
               onClick={() => setIsDark(!isDark)}
               className={`p-3 rounded-xl transition-all duration-300 ${isDark
                  ? 'bg-slate-800 text-yellow-400 hover:bg-slate-700 shadow-lg shadow-slate-900/50'
                  : 'bg-white text-slate-700 hover:bg-slate-50 shadow-lg shadow-slate-200/50'
                  }`}>
               {isDark ? '☀️' : '🌙'}
            </button>

            {/* Logo - Right (You can replace this with your Logo component) */}
            <Link to={'/'}>
               <div
                  className={`px-4 py-2 rounded-xl cursor-pointer ${isDark
                     ? 'bg-slate-800/80 backdrop-blur-sm'
                     : 'bg-white/80 backdrop-blur-sm shadow-lg shadow-slate-200/50'
                     }`}>
                  <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                     {' '}
                     <Image src={Logo} alt="Zeronine Logo" w="150px" />
                  </span>
               </div>
            </Link>
         </div>

         <div className="min-h-screen flex">
            {/* Left Side - Branding */}
            <div
               className={`hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden ${isDark
                  ? 'bg-gradient-to-br from-indigo-600 to-purple-700'
                  : 'bg-gradient-to-br from-indigo-600 to-blue-700'
                  }`}>
               <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
                  <div
                     className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"
                     style={{ animationDelay: '1s' }}></div>
               </div>

               <div className="relative z-10 flex flex-col justify-between px-12 xl:px-20 py-12 text-white w-full">
                  <div className="flex-1 flex flex-col justify-center">
                     <h2 className="text-4xl xl:text-5xl font-bold mb-6 leading-tight">
                        Streamline Your
                        <br />
                        Financial Operations
                     </h2>

                     <p className="text-lg text-white/90 mb-12 max-w-md">
                        Powerful accounting software for modern businesses. Manage invoices, expenses, and reports with
                        ease.
                     </p>

                     <div className="space-y-6">
                        <div className="flex items-start gap-4">
                           <div className="p-2 bg-white/20 rounded-lg mt-1">
                              <CheckCircle2 className="w-5 h-5" />
                           </div>
                           <div>
                              <h3 className="font-semibold mb-1">Real-time Analytics</h3>
                              <p className="text-white/80 text-sm">
                                 Track your business performance with live dashboards
                              </p>
                           </div>
                        </div>

                        <div className="flex items-start gap-4">
                           <div className="p-2 bg-white/20 rounded-lg mt-1">
                              <TrendingUp className="w-5 h-5" />
                           </div>
                           <div>
                              <h3 className="font-semibold mb-1">Smart Automation</h3>
                              <p className="text-white/80 text-sm">Automate recurring tasks and save valuable time</p>
                           </div>
                        </div>

                        <div className="flex items-start gap-4">
                           <div className="p-2 bg-white/20 rounded-lg mt-1">
                              <Lock className="w-5 h-5" />
                           </div>
                           <div>
                              <h3 className="font-semibold mb-1">Bank-level Security</h3>
                              <p className="text-white/80 text-sm">
                                 Your financial data is protected with enterprise-grade encryption
                              </p>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            {/* Right Side - Sign In Form */}
            <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 sm:p-8">
               <div className="w-full max-w-md">
                  {/* Sign In Card */}
                  <div
                     className={`rounded-2xl shadow-2xl p-8 sm:p-10 transition-colors duration-300 ${isDark
                        ? 'bg-slate-800/50 backdrop-blur-xl border border-slate-700/50'
                        : 'bg-white/80 backdrop-blur-xl border border-white/20'
                        }`}>
                     <div className="mb-8">
                        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                           Welcome To Zeronine
                        </h2>
                        <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                           Enter your email and password to sign in!
                        </p>
                     </div>

                     <div className="space-y-5">
                        {/* Email Input */}
                        <div>
                           <label
                              className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'
                                 }`}>
                              Email <span className="text-red-500">*</span>
                           </label>
                           <div className="relative">
                              <Mail
                                 className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none ${isDark ? 'text-slate-500' : 'text-slate-400'
                                    }`}
                              />
                              <input
                                 type="email"
                                 value={formData.email}
                                 onChange={(e) => {
                                    setFormData({ ...formData, email: e.target.value });
                                    setEmailForOtp(e.target.value);
                                    if (errors.email) setErrors({ ...errors, email: '' });
                                 }}
                                 onKeyDown={handleKeyDown}
                                 placeholder="Email"
                                 className={`w-full pl-12 pr-4 py-3.5 rounded-xl transition-all duration-200 ${isDark
                                    ? 'bg-slate-900/50 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                    : 'bg-white border-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                    } ${errors.email ? 'border-red-500' : ''}`}
                              />
                           </div>
                           {errors.email && <p className="mt-1.5 text-sm text-red-500">{errors.email}</p>}
                        </div>

                        {/* Password Input */}
                        <div>
                           <label
                              className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'
                                 }`}>
                              Password <span className="text-red-500">*</span>
                           </label>
                           <div className="relative">
                              <Lock
                                 className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none ${isDark ? 'text-slate-500' : 'text-slate-400'
                                    }`}
                              />
                              <input
                                 type={showPassword ? 'text' : 'password'}
                                 value={formData.password}
                                 onChange={(e) => {
                                    setFormData({ ...formData, password: e.target.value });
                                    if (errors.password) setErrors({ ...errors, password: '' });
                                 }}
                                 onKeyDown={handleKeyDown}
                                 placeholder="Min. 6 characters"
                                 className={`w-full pl-12 pr-12 py-3.5 rounded-xl transition-all duration-200 ${isDark
                                    ? 'bg-slate-900/50 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                    : 'bg-white border-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                    } ${errors.password ? 'border-red-500' : ''}`}
                              />
                              <button
                                 type="button"
                                 onClick={() => setShowPassword(!showPassword)}
                                 className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${isDark
                                    ? 'text-slate-500 hover:text-slate-300'
                                    : 'text-slate-400 hover:text-slate-600'
                                    }`}>
                                 {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                              </button>
                           </div>
                           {errors.password && <p className="mt-1.5 text-sm text-red-500">{errors.password}</p>}
                        </div>

                        {/* OTP Section for 2FA */}
                        {otpShow && (
                           <div
                              className={`p-5 rounded-xl space-y-4 ${isDark
                                 ? 'bg-slate-900/50 border border-slate-700'
                                 : 'bg-indigo-50 border border-indigo-100'
                                 }`}>
                              <div>
                                 <label
                                    className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'
                                       }`}>
                                    Choose OTP Method
                                 </label>

                                 <div className="flex items-center justify-between mb-4">
                                    <div className="flex gap-3">
                                       {['email', 'mobile', 'whatsapp'].map((method) => (
                                          <button
                                             key={method}
                                             type="button"
                                             onClick={() => setOtpMethod(method)}
                                             className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${otpMethod === method
                                                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                                                : isDark
                                                   ? 'bg-slate-800 text-slate-400 hover:text-slate-300'
                                                   : 'bg-white text-slate-600 hover:text-slate-900'
                                                }`}>
                                             {method.charAt(0).toUpperCase() + method.slice(1)}
                                          </button>
                                       ))}
                                    </div>
                                 </div>
                                 <button
                                    type="button"
                                    onClick={handleSendOtp}
                                    className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${isDark
                                       ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                       : 'bg-white text-indigo-600 hover:bg-indigo-50 border border-indigo-200'
                                       }`}>
                                    {otpSent ? <RefreshCw className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                                    {otpSent ? 'Resend OTP' : 'Send OTP'}
                                 </button>
                              </div>

                              <div>
                                 <label
                                    className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'
                                       }`}>
                                    Enter OTP
                                 </label>
                                 <div className="flex gap-2 justify-center">
                                    {[0, 1, 2, 3, 4, 5].map((index) => (
                                       <input
                                          key={index}
                                          id={`twoFactor-otp-${index}`}
                                          type="text"
                                          maxLength={1}
                                          value={twoFactorOtp[index] || ''}
                                          onChange={(e) => {
                                             const value = e.target.value.replace(/\D/g, '');
                                             handleTwoFactorOtpChange(index, value);
                                          }}
                                          onKeyDown={(e) => {
                                             handleTwoFactorOtpBackspace(index, e);
                                             if (e.key === 'Enter') {
                                                handleKeyDown(e);
                                             }
                                          }}
                                          onPaste={handleTwoFactorOtpPaste}
                                          className={`w-12 h-14 text-center text-xl font-bold rounded-xl transition-all duration-200 ${isDark
                                             ? 'bg-slate-900/50 border-2 border-slate-700 text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                             : 'bg-white border-2 border-slate-200 text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                             } ${errors.otp ? 'border-red-500' : ''}`}
                                       />
                                    ))}
                                 </div>
                                 {errors.otp && <p className="mt-1.5 text-sm text-red-500 text-center">{errors.otp}</p>}
                              </div>
                           </div>
                        )}

                        {/* Submit Button */}
                        <button
                           type="button"
                           onClick={handleSubmit}
                           disabled={isLoading}
                           className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${isLoading
                              ? 'bg-slate-400 cursor-not-allowed'
                              : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 shadow-lg shadow-indigo-600/30 hover:shadow-xl hover:shadow-indigo-600/40 hover:-translate-y-0.5'
                              }`}>
                           {isLoading ? (
                              <>
                                 <RefreshCw className="w-5 h-5 animate-spin" />
                                 Signing in...
                              </>
                           ) : (
                              'Sign In'
                           )}
                        </button>

                        <div
                           className={`pt-5 text-center text-sm border-t ${isDark ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-600'
                              }`}>
                           Not registered yet?{' '}
                           <button
                              type="button"
                              onClick={() => (window.location.href = '/#pricing')}
                              className={`font-semibold transition-colors ${isDark
                                 ? 'text-indigo-400 hover:text-indigo-300'
                                 : 'text-indigo-600 hover:text-indigo-700'
                                 }`}>
                              Create an Account
                           </button>{' '}
                           Or{' '}
                           <button
                              type="button"
                              onClick={() => setShowForgotModal(true)}
                              className={`font-semibold transition-colors ${isDark
                                 ? 'text-indigo-400 hover:text-indigo-300'
                                 : 'text-indigo-600 hover:text-indigo-700'
                                 }`}>
                              Forgot Password
                           </button>
                           ?
                        </div>
                     </div>
                  </div>

                  {/* App Store Buttons at Bottom */}
                  <div className="mt-12 text-center">
                     <h6 className={`font-semibold mb-4 text-sm ${isDark ? 'text-white/90' : 'text-gray-700'}`}>
                        Download Our App
                     </h6>
                     <div className="flex flex-row gap-3 justify-center flex-wrap">
                        <a
                           href="https://play.google.com/store/apps/details?id=com.ss.zeronine.app"
                           target="_blank"
                           rel="noopener noreferrer"
                           className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 transition-all duration-300 ${isDark
                              ? 'bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30'
                              : 'bg-white border border-gray-200 hover:bg-gray-50 shadow-md hover:shadow-lg'
                              }`}>
                           <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                              <path
                                 d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92z"
                                 fill={isDark ? "#32BBFF" : "#4285F4"}
                              />
                              <path
                                 d="M14.5 12.707l3.842 3.842-10.25 5.902 6.408-9.744z"
                                 fill={isDark ? "#F4B400" : "#EA4335"}
                              />
                              <path
                                 d="M18.342 16.549l3.654-2.103c.394-.227.658-.594.658-1.063 0-.469-.264-.836-.658-1.063l-3.654-2.103-3.842 3.841 3.842 3.491z"
                                 fill={isDark ? "#FF5B57" : "#34A853"}
                              />
                              <path
                                 d="M8.092 1.348L18.342 7.25l-3.842 3.842L8.092 1.348z"
                                 fill={isDark ? "#60D669" : "#FBBC05"}
                              />
                           </svg>
                           <div className="text-left">
                              <p className={`text-xs ${isDark ? 'text-white/70' : 'text-gray-500'}`}>GET IT ON</p>
                              <p className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Google Play</p>
                           </div>
                        </a>

                        <a
                           href="https://apps.apple.com/in/app/09-ai/id6747242947"
                           target="_blank"
                           rel="noopener noreferrer"
                           className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 transition-all duration-300 ${isDark
                              ? 'bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30'
                              : 'bg-white border border-gray-200 hover:bg-gray-50 shadow-md hover:shadow-lg'
                              }`}>
                           <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                              <defs>
                                 <linearGradient id="appleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor={isDark ? "#FFFFFF" : "#000000"} />
                                    <stop offset="100%" stopColor={isDark ? "#CCCCCC" : "#333333"} />
                                 </linearGradient>
                              </defs>
                              <path
                                 d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                                 fill={isDark ? "url(#appleGradient)" : "#000000"}
                              />
                           </svg>
                           <div className="text-left">
                              <p className={`text-xs ${isDark ? 'text-white/70' : 'text-gray-500'}`}>Download on the</p>
                              <p className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>App Store</p>
                           </div>
                        </a>
                     </div>
                  </div>

                  <div className="mt-6 text-center">
                     <h6 className="font-semibold mb-3 text-sm">Follow Us</h6>
                     <div className="flex gap-3 justify-center">
                        {/* Instagram */}
                        <a
                           href="https://www.instagram.com/zeronine.ai_?igsh=bzI4ODB2cGFnYWY4"
                           target="_blank"
                           rel="noopener noreferrer"
                           className="social-icon inline-flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:shadow-lg hover:shadow-pink-500/50 transition-shadow"
                           aria-label="Follow us on Instagram">
                           <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                           </svg>
                        </a>

                        {/* YouTube */}
                        <a
                           href="https://www.youtube.com/@ZeronineAiTech"
                           target="_blank"
                           rel="noopener noreferrer"
                           className="social-icon inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-600 hover:shadow-lg hover:shadow-red-500/50 transition-shadow"
                           aria-label="Follow us on YouTube">
                           <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                           </svg>
                        </a>

                        {/* Facebook */}
                        <a
                           href="https://www.facebook.com/profile.php?id=61586899329878"
                           target="_blank"
                           rel="noopener noreferrer"
                           className="social-icon inline-flex items-center justify-center w-9 h-9 rounded-full bg-blue-600 hover:shadow-lg hover:shadow-blue-500/50 transition-shadow"
                           aria-label="Follow us on Facebook">
                           <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                           </svg>
                        </a>

                        {/* LinkedIn */}
                        <a
                           href="https://www.linkedin.com/in/zeronine-ai-3864603a8?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app"
                           target="_blank"
                           rel="noopener noreferrer"
                           className="social-icon inline-flex items-center justify-center w-9 h-9 rounded-full bg-blue-700 hover:shadow-lg hover:shadow-blue-700/50 transition-shadow"
                           aria-label="Follow us on LinkedIn">
                           <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                           </svg>
                        </a>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         {/* Forgot Password Modal */}
         {showForgotModal && (
            <div
               className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
               onClick={() => setShowForgotModal(false)}>
               <div
                  className={`w-full max-w-md rounded-2xl shadow-2xl p-8 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'
                     }`}
                  onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-6">
                     <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Forgot Password
                     </h3>
                     <button
                        onClick={() => setShowForgotModal(false)}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-600'
                           }`}>
                        <X className="w-5 h-5" />
                     </button>
                  </div>

                  <div className="space-y-4">
                     <div className="flex gap-3">
                        <input
                           type="email"
                           value={email}
                           onChange={(e) => setEmail(e.target.value.toLowerCase())}
                           placeholder="Enter your email"
                           autoFocus
                           className={`flex-1 px-4 py-3 rounded-xl transition-all duration-200 ${isDark
                              ? 'bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none'
                              : 'bg-white border-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none'
                              }`}
                        />
                        <button
                           type="button"
                           onClick={sendOtp}
                           className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap">
                           Send OTP
                        </button>
                     </div>

                     {showOtpFields && (
                        <>
                           <div>
                              <label
                                 className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'
                                    }`}>
                                 Enter OTP
                              </label>
                              <div className="flex gap-2 justify-center">
                                 {[0, 1, 2, 3, 4, 5].map((index) => (
                                    <input
                                       key={index}
                                       id={`forgot-otp-${index}`}
                                       type="text"
                                       maxLength={1}
                                       value={forgotPasswordOtp[index] || ''}
                                       onChange={(e) => {
                                          const value = e.target.value.replace(/\D/g, '');
                                          handleForgotPasswordOtpChange(index, value);
                                       }}
                                       onKeyDown={(e) => {
                                          handleForgotPasswordOtpBackspace(index, e);
                                          if (e.key === 'Enter') {
                                             handleKeyDown(e);
                                          }
                                       }}
                                       onPaste={handleForgotPasswordOtpPaste}
                                       className={`w-12 h-14 text-center text-xl font-bold rounded-xl transition-all duration-200 ${isDark
                                          ? 'bg-slate-900/50 border-2 border-slate-700 text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                          : 'bg-white border-2 border-slate-200 text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                          } ${errors.otp ? 'border-red-500' : ''}`}
                                    />
                                 ))}
                              </div>
                              {errors.otp && <p className="mt-1.5 text-sm text-red-500 text-center">{errors.otp}</p>}
                           </div>
                           <div className="relative">
                              <input
                                 type={showPassword ? 'text' : 'password'}
                                 value={newPassword}
                                 onChange={(e) => setNewPassword(e.target.value)}
                                 placeholder="Enter New Password"
                                 className={`w-full px-4 py-3 pr-12 rounded-xl transition-all duration-200 ${isDark
                                    ? 'bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none'
                                    : 'bg-white border-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none'
                                    }`}
                              />
                              <button
                                 type="button"
                                 onClick={() => setShowPassword(!showPassword)}
                                 className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${isDark
                                    ? 'text-slate-500 hover:text-slate-300'
                                    : 'text-slate-400 hover:text-slate-600'
                                    }`}>
                                 {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                              </button>
                           </div>
                        </>
                     )}
                  </div>

                  <div className="flex gap-3 mt-6">
                     {email && forgotPasswordOtp && newPassword && (
                        <button
                           onClick={resetPassword}
                           className="flex-1 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition-all">
                           Reset Password
                        </button>
                     )}
                     <button
                        onClick={() => {
                           setShowForgotModal(false);
                           setEmail('');
                           setForgotPasswordOtp('');
                           setNewPassword('');
                           setShowOtpFields(false);
                        }}
                        className={`${email && forgotPasswordOtp && newPassword ? 'flex-1' : 'w-full'
                           } py-3 rounded-xl font-semibold transition-all ${isDark
                              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                           }`}>
                        Cancel
                     </button>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
}

export default SignIn;
